import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useAuth, authFetch } from "@/App";
import { API } from "@/config";
import PageLayout, { PageHeader, Card, EmptyState } from "@/components/PageLayout";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { QRCodeSVG } from "qrcode.react";
import Barcode from "react-barcode";
import PhotoUploader from "@/components/PhotoUploader";
import ImageCropper from "@/components/ImageCropper";
import DocumentUploader from "@/components/DocumentUploader";
import { toast } from "sonner";
import { 
  Dog, Cat, Bird, Rabbit, PawPrint, Plus, Edit, Trash2, QrCode, 
  Syringe, FileText, Calendar, Weight, Fingerprint, Heart, 
  MapPin, User, Share2, Download, ChevronRight, Clock, CheckCircle,
  AlertCircle, Camera, Upload, Eye, AlertTriangle, CreditCard
} from "lucide-react";

const speciesIcons = {
  dog: Dog,
  cat: Cat,
  bird: Bird,
  rabbit: Rabbit,
  other: PawPrint
};

const speciesNames = {
  dog: "Cane",
  cat: "Gatto",
  bird: "Uccello",
  rabbit: "Coniglio",
  other: "Altro"
};

export default function Pets() {
  const { petId } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [pets, setPets] = useState([]);
  const [selectedPet, setSelectedPet] = useState(null);
  const [vaccines, setVaccines] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddPet, setShowAddPet] = useState(false);
  const [showEditPet, setShowEditPet] = useState(false);
  const [showQRCode, setShowQRCode] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showDocUploader, setShowDocUploader] = useState(false);
  const [showAddVaccine, setShowAddVaccine] = useState(false);
  const [activeTab, setActiveTab] = useState("info");
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // Photo cropper states
  const [showCropper, setShowCropper] = useState(false);
  const [cropImage, setCropImage] = useState(null);
  
  // New vaccine form
  const [newVaccine, setNewVaccine] = useState({
    vaccine_name: "",
    date_administered: "",
    expiry_date: "",
    vet_name: "",
    batch_number: ""
  });
  
  const [newPet, setNewPet] = useState({
    name: "",
    species: "dog",
    breed: "",
    birth_date: "",
    weight: "",
    microchip: "",
    color: "",
    notes: ""
  });

  useEffect(() => {
    fetchPets();
  }, []);

  useEffect(() => {
    if (petId && pets.length > 0) {
      const pet = pets.find(p => p.pet_id === petId);
      if (pet) {
        setSelectedPet(pet);
        fetchPetDetails(pet.pet_id);
      }
    } else if (!petId && pets.length > 0) {
      setSelectedPet(pets[0]);
      fetchPetDetails(pets[0].pet_id);
    }
  }, [petId, pets]);

  const fetchPets = async () => {
    try {
      const response = await authFetch(`${API}/pets`);
      if (response.ok) {
        const data = await response.json();
        setPets(data);
      }
    } catch (error) {
      console.error("Error fetching pets:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchPetDetails = async (petId) => {
    try {
      const [vaccinesRes, documentsRes] = await Promise.all([
        authFetch(`${API}/vaccines?pet_id=${petId}`),
        authFetch(`${API}/documents?pet_id=${petId}`)
      ]);
      
      if (vaccinesRes.ok) setVaccines(await vaccinesRes.json());
      if (documentsRes.ok) setDocuments(await documentsRes.json());
    } catch (error) {
      console.error("Error fetching pet details:", error);
    }
  };

  const handleAddVaccine = async (e) => {
    e.preventDefault();
    if (!selectedPet) return;
    setIsSubmitting(true);
    try {
      const response = await authFetch(`${API}/vaccines`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...newVaccine,
          pet_id: selectedPet.pet_id
        })
      });
      if (response.ok) {
        const vaccine = await response.json();
        setVaccines([...vaccines, vaccine]);
        setShowAddVaccine(false);
        setNewVaccine({ vaccine_name: "", date_administered: "", expiry_date: "", vet_name: "", batch_number: "" });
        toast.success("Vaccino aggiunto!");
      }
    } catch (error) {
      toast.error("Errore nell'aggiunta del vaccino");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteVaccine = async (vaccineId) => {
    if (!window.confirm("Sei sicuro di voler eliminare questo vaccino?")) return;
    try {
      const response = await authFetch(`${API}/vaccines/${vaccineId}`, { method: 'DELETE' });
      if (response.ok) {
        setVaccines(vaccines.filter(v => v.vaccine_id !== vaccineId));
        toast.success("Vaccino eliminato");
      }
    } catch (error) {
      toast.error("Errore di connessione");
    }
  };

  const handleDeleteDocument = async (documentId) => {
    if (!window.confirm("Sei sicuro di voler eliminare questo documento?")) return;
    try {
      const response = await authFetch(`${API}/documents/${documentId}`, { method: 'DELETE' });
      if (response.ok) {
        setDocuments(documents.filter(d => d.document_id !== documentId));
        toast.success("Documento eliminato");
      }
    } catch (error) {
      toast.error("Errore di connessione");
    }
  };

  const getVaccineStatus = (expiryDate) => {
    if (!expiryDate) return { status: "unknown", label: "Sconosciuto", color: "gray" };
    const expiry = new Date(expiryDate);
    const now = new Date();
    const daysUntil = Math.ceil((expiry - now) / (1000 * 60 * 60 * 24));
    
    if (daysUntil < 0) return { status: "expired", label: "Scaduto", color: "red" };
    if (daysUntil <= 30) return { status: "expiring", label: `Scade tra ${daysUntil}g`, color: "amber" };
    return { status: "valid", label: "Valido", color: "emerald" };
  };

  const handleAddPet = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      // Clean data before sending - convert empty strings to null and weight to float
      const cleanData = {
        name: newPet.name,
        species: newPet.species,
        breed: newPet.breed || null,
        birth_date: newPet.birth_date || null,
        weight: newPet.weight ? parseFloat(newPet.weight) : null,
        microchip: newPet.microchip || null,
        color: newPet.color || null,
        notes: newPet.notes || null
      };
      const response = await authFetch(`${API}/pets`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(cleanData)
      });
      if (response.ok) {
        const pet = await response.json();
        setPets([...pets, pet]);
        setSelectedPet(pet);
        setShowAddPet(false);
        setNewPet({ name: "", species: "dog", breed: "", birth_date: "", weight: "", microchip: "", color: "", notes: "" });
        toast.success("Animale aggiunto!");
      }
    } catch (error) {
      toast.error("Errore nell'aggiungere l'animale");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleUpdatePet = async (e) => {
    e.preventDefault();
    if (!selectedPet) return;
    setIsSubmitting(true);
    try {
      // Clean data before sending - convert empty strings to null and weight to float
      const cleanData = {
        name: newPet.name,
        species: newPet.species,
        breed: newPet.breed || null,
        birth_date: newPet.birth_date || null,
        weight: newPet.weight ? parseFloat(newPet.weight) : null,
        microchip: newPet.microchip || null,
        color: newPet.color || null,
        notes: newPet.notes || null
      };
      const response = await authFetch(`${API}/pets/${selectedPet.pet_id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(cleanData)
      });
      if (response.ok) {
        const updated = await response.json();
        setPets(pets.map(p => p.pet_id === updated.pet_id ? updated : p));
        setSelectedPet(updated);
        setShowEditPet(false);
        toast.success("Animale aggiornato!");
      }
    } catch (error) {
      toast.error("Errore nell'aggiornamento");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeletePet = async () => {
    if (!selectedPet) return;
    try {
      const response = await authFetch(`${API}/pets/${selectedPet.pet_id}`, {
        method: 'DELETE'
      });
      if (response.ok) {
        const remaining = pets.filter(p => p.pet_id !== selectedPet.pet_id);
        setPets(remaining);
        setSelectedPet(remaining[0] || null);
        setShowDeleteConfirm(false);
        toast.success("Animale rimosso");
      }
    } catch (error) {
      toast.error("Errore nella rimozione");
    }
  };

  const handlePhotoUpload = async (blob) => {
    if (!selectedPet) return;
    const formData = new FormData();
    formData.append('file', blob, 'photo.png');
    
    try {
      const response = await authFetch(`${API}/upload/pet-photo/${selectedPet.pet_id}`, {
        method: 'POST',
        body: formData
      });
      if (response.ok) {
        const data = await response.json();
        // Update pet with new photo URL
        const updatedPet = { ...selectedPet, photo_url: data.photo_url };
        setPets(pets.map(p => p.pet_id === selectedPet.pet_id ? updatedPet : p));
        setSelectedPet(updatedPet);
        toast.success("Foto aggiornata!");
      } else {
        const error = await response.json();
        toast.error(error.detail || "Errore nel caricamento foto");
      }
    } catch (error) {
      toast.error("Errore di connessione");
    }
  };

  const getAge = (birthDate) => {
    if (!birthDate) return null;
    const birth = new Date(birthDate);
    const now = new Date();
    const years = now.getFullYear() - birth.getFullYear();
    const months = now.getMonth() - birth.getMonth();
    if (years > 0) return `${years} ann${years === 1 ? 'o' : 'i'}`;
    if (months > 0) return `${months} mes${months === 1 ? 'e' : 'i'}`;
    return "Appena nato";
  };

  // Helper to get full photo URL
  const getPhotoUrl = (photoUrl) => {
    if (!photoUrl) return null;
    if (photoUrl.startsWith('http')) return photoUrl;
    if (photoUrl.startsWith('/')) return `${API.replace('/api', '')}${photoUrl}`;
    return photoUrl;
  };

  const shareUrl = selectedPet ? `${window.location.origin}/pet/${selectedPet.pet_id}/public` : '';

  const SpeciesIcon = selectedPet ? (speciesIcons[selectedPet.species] || PawPrint) : PawPrint;

  if (loading) {
    return (
      <PageLayout>
        <div className="flex items-center justify-center h-64">
          <div className="w-8 h-8 border-4 border-[#FF6B4A] border-t-transparent rounded-full animate-spin" />
        </div>
      </PageLayout>
    );
  }

  return (
    <PageLayout data-testid="pets-page">
      <PageHeader 
        title="I Miei Animali"
        subtitle={`${pets.length} animale${pets.length !== 1 ? 'i' : ''} registrat${pets.length !== 1 ? 'i' : 'o'}`}
        icon={Dog}
        iconColor="#FF6B4A"
        action={
          <Button 
            onClick={() => setShowAddPet(true)}
            className="bg-[#FF6B4A] hover:bg-[#E05A3A] text-white rounded-xl px-6"
          >
            <Plus size={20} className="mr-2" />
            Aggiungi Animale
          </Button>
        }
      />

      {pets.length === 0 ? (
        <EmptyState
          icon={PawPrint}
          title="Nessun animale registrato"
          description="Aggiungi il tuo primo amico a quattro zampe per iniziare a gestire il suo fascicolo sanitario."
          action={
            <Button 
              onClick={() => setShowAddPet(true)}
              className="bg-[#FF6B4A] hover:bg-[#E05A3A] text-white rounded-xl px-6"
            >
              <Plus size={20} className="mr-2" />
              Aggiungi Animale
            </Button>
          }
        />
      ) : (
        <div className="grid grid-cols-12 gap-6">
          {/* Pets List - Left Sidebar */}
          <div className="col-span-12 lg:col-span-4 xl:col-span-3">
            <Card padding="p-4">
              <div className="space-y-2">
                {pets.map((pet) => {
                  const Icon = speciesIcons[pet.species] || PawPrint;
                  const isSelected = selectedPet?.pet_id === pet.pet_id;
                  return (
                    <button
                      key={pet.pet_id}
                      onClick={() => {
                        setSelectedPet(pet);
                        navigate(`/pets/${pet.pet_id}`, { replace: true });
                      }}
                      className={`w-full flex items-center gap-3 p-3 rounded-xl transition-all ${
                        isSelected 
                          ? 'bg-[#FF6B4A]/10 border-2 border-[#FF6B4A]' 
                          : 'hover:bg-[#F5F5F7] dark:hover:bg-white/5 border-2 border-transparent'
                      }`}
                    >
                      {pet.photo_url ? (
                        <img 
                          src={getPhotoUrl(pet.photo_url)} 
                          alt={pet.name}
                          className="w-12 h-12 rounded-xl object-cover"
                        />
                      ) : (
                        <div className="w-12 h-12 rounded-xl bg-[#F5F5F7] dark:bg-white/10 flex items-center justify-center">
                          <Icon className="text-[#86868B]" size={24} />
                        </div>
                      )}
                      <div className="flex-1 text-left">
                        <p className="font-semibold text-[#1D1D1F] dark:text-white">{pet.name}</p>
                        <p className="text-sm text-[#86868B]">{speciesNames[pet.species]} • {pet.breed || 'Razza mista'}</p>
                      </div>
                      <ChevronRight size={18} className="text-[#86868B]" />
                    </button>
                  );
                })}
              </div>
            </Card>
          </div>

          {/* Pet Details - Main Content */}
          {selectedPet && (
            <div className="col-span-12 lg:col-span-8 xl:col-span-9 space-y-6">
              {/* Pet Header Card */}
              <Card className="relative overflow-hidden">
                <div className="flex flex-col md:flex-row gap-6">
                  {/* Photo */}
                  <div className="relative group">
                    {selectedPet.photo_url ? (
                      <img 
                        src={getPhotoUrl(selectedPet.photo_url)} 
                        alt={selectedPet.name}
                        className="w-40 h-40 rounded-2xl object-cover"
                      />
                    ) : (
                      <div className="w-40 h-40 rounded-2xl bg-gradient-to-br from-[#FF6B4A]/20 to-[#FF8F6B]/20 flex items-center justify-center">
                        <SpeciesIcon className="text-[#FF6B4A]" size={64} />
                      </div>
                    )}
                    <button
                      onClick={() => document.getElementById('pet-photo-input').click()}
                      className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity rounded-2xl flex items-center justify-center"
                    >
                      <Camera className="text-white" size={32} />
                    </button>
                    <input
                      id="pet-photo-input"
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={(e) => {
                        if (e.target.files?.[0]) {
                          const reader = new FileReader();
                          reader.onload = (ev) => {
                            setCropImage(ev.target.result);
                            setShowCropper(true);
                          };
                          reader.readAsDataURL(e.target.files[0]);
                        }
                      }}
                    />
                  </div>

                  {/* Info */}
                  <div className="flex-1">
                    <div className="flex items-start justify-between">
                      <div>
                        <h2 className="text-3xl font-bold text-[#1D1D1F] dark:text-white">{selectedPet.name}</h2>
                        <p className="text-[#86868B] mt-1">
                          {speciesNames[selectedPet.species]} • {selectedPet.breed || 'Razza mista'}
                          {selectedPet.birth_date && ` • ${getAge(selectedPet.birth_date)}`}
                        </p>
                      </div>
                      <div className="flex gap-2">
                        <Button
                          variant="outline"
                          size="icon"
                          onClick={() => {
                            setNewPet({ ...selectedPet });
                            setShowEditPet(true);
                          }}
                          className="rounded-xl"
                        >
                          <Edit size={18} />
                        </Button>
                        <Button
                          variant="outline"
                          size="icon"
                          onClick={() => setShowQRCode(true)}
                          className="rounded-xl"
                        >
                          <QrCode size={18} />
                        </Button>
                        <Button
                          variant="outline"
                          size="icon"
                          onClick={() => setShowDeleteConfirm(true)}
                          className="rounded-xl text-red-500 hover:bg-red-50"
                        >
                          <Trash2 size={18} />
                        </Button>
                      </div>
                    </div>

                    {/* Quick Stats */}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-6">
                      {selectedPet.weight && (
                        <div className="flex items-center gap-3 p-3 bg-[#F5F5F7] dark:bg-white/5 rounded-xl">
                          <Weight className="text-[#FF6B4A]" size={20} />
                          <div>
                            <p className="text-xs text-[#86868B]">Peso</p>
                            <p className="font-semibold text-[#1D1D1F] dark:text-white">{selectedPet.weight} kg</p>
                          </div>
                        </div>
                      )}
                      {selectedPet.microchip && (
                        <div className="flex items-center gap-3 p-3 bg-[#F5F5F7] dark:bg-white/5 rounded-xl">
                          <Fingerprint className="text-[#6366F1]" size={20} />
                          <div>
                            <p className="text-xs text-[#86868B]">Microchip</p>
                            <p className="font-semibold text-[#1D1D1F] dark:text-white text-xs truncate max-w-[100px]">{selectedPet.microchip}</p>
                          </div>
                        </div>
                      )}
                      {selectedPet.color && (
                        <div className="flex items-center gap-3 p-3 bg-[#F5F5F7] dark:bg-white/5 rounded-xl">
                          <Heart className="text-pink-500" size={20} />
                          <div>
                            <p className="text-xs text-[#86868B]">Colore</p>
                            <p className="font-semibold text-[#1D1D1F] dark:text-white">{selectedPet.color}</p>
                          </div>
                        </div>
                      )}
                      {selectedPet.birth_date && (
                        <div className="flex items-center gap-3 p-3 bg-[#F5F5F7] dark:bg-white/5 rounded-xl">
                          <Calendar className="text-emerald-500" size={20} />
                          <div>
                            <p className="text-xs text-[#86868B]">Nato il</p>
                            <p className="font-semibold text-[#1D1D1F] dark:text-white">
                              {new Date(selectedPet.birth_date).toLocaleDateString('it-IT')}
                            </p>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </Card>

              {/* Quick Actions */}
              <Card>
                <Tabs value={activeTab} onValueChange={setActiveTab}>
                  <TabsList className="grid w-full grid-cols-3 bg-[#F5F5F7] rounded-xl p-1">
                    <TabsTrigger value="info" className="rounded-lg data-[state=active]:bg-white">
                      <CreditCard size={16} className="mr-2" />
                      ID & Condivisione
                    </TabsTrigger>
                    <TabsTrigger value="vaccines" className="rounded-lg data-[state=active]:bg-white">
                      <Syringe size={16} className="mr-2" />
                      Vaccini ({vaccines.length})
                    </TabsTrigger>
                    <TabsTrigger value="documents" className="rounded-lg data-[state=active]:bg-white">
                      <FileText size={16} className="mr-2" />
                      Documenti ({documents.length})
                    </TabsTrigger>
                  </TabsList>

                  {/* ID & Sharing Tab */}
                  <TabsContent value="info" className="mt-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div className="bg-[#F5F5F7] rounded-2xl p-6">
                        <h4 className="font-semibold text-[#1D1D1F] mb-4 flex items-center gap-2">
                          <QrCode size={18} className="text-[#FF6B4A]" />
                          QR Code Condivisione
                        </h4>
                        <div className="flex flex-col items-center">
                          <div className="p-3 bg-white rounded-xl">
                            <QRCodeSVG value={shareUrl} size={120} level="H" />
                          </div>
                          <p className="text-xs text-[#86868B] mt-2 text-center">
                            Scansiona per il profilo pubblico
                          </p>
                          <Button 
                            variant="outline" 
                            className="mt-3 rounded-xl"
                            onClick={() => {
                              navigator.clipboard.writeText(shareUrl);
                              toast.success("Link copiato!");
                            }}
                          >
                            <Share2 size={14} className="mr-2" />
                            Copia Link
                          </Button>
                        </div>
                      </div>

                      <div className="bg-[#F5F5F7] rounded-2xl p-6">
                        <h4 className="font-semibold text-[#1D1D1F] mb-4 flex items-center gap-2">
                          <Fingerprint size={18} className="text-[#6366F1]" />
                          Barcode Microchip
                        </h4>
                        <div className="flex flex-col items-center">
                          {selectedPet.microchip ? (
                            <>
                              <div className="p-3 bg-white rounded-xl">
                                <Barcode 
                                  value={selectedPet.microchip} 
                                  width={1.2}
                                  height={50}
                                  fontSize={10}
                                  margin={5}
                                />
                              </div>
                              <p className="text-xs text-[#86868B] mt-2">
                                {selectedPet.microchip}
                              </p>
                            </>
                          ) : (
                            <div className="text-center py-4">
                              <Fingerprint size={40} className="mx-auto mb-2 text-[#D1D1D6]" />
                              <p className="text-sm text-[#86868B]">Nessun microchip</p>
                              <Button
                                variant="outline"
                                size="sm"
                                className="mt-2 rounded-lg"
                                onClick={() => {
                                  setNewPet({ ...selectedPet });
                                  setShowEditPet(true);
                                }}
                              >
                                Aggiungi
                              </Button>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </TabsContent>

                  {/* Vaccines Tab */}
                  <TabsContent value="vaccines" className="mt-6">
                    <div className="flex justify-between items-center mb-4">
                      <h4 className="font-semibold text-[#1D1D1F]">Vaccinazioni</h4>
                      <Button 
                        onClick={() => setShowAddVaccine(true)}
                        className="bg-emerald-500 hover:bg-emerald-600 rounded-xl"
                        size="sm"
                      >
                        <Plus size={16} className="mr-1" />
                        Aggiungi
                      </Button>
                    </div>
                    
                    {vaccines.length === 0 ? (
                      <div className="text-center py-8 bg-[#F5F5F7] rounded-2xl">
                        <Syringe size={40} className="mx-auto mb-3 text-[#D1D1D6]" />
                        <p className="text-[#86868B]">Nessun vaccino registrato</p>
                        <Button 
                          onClick={() => setShowAddVaccine(true)}
                          variant="outline"
                          className="mt-3 rounded-xl"
                        >
                          Aggiungi primo vaccino
                        </Button>
                      </div>
                    ) : (
                      <div className="space-y-3">
                        {vaccines.map((vaccine) => {
                          const status = getVaccineStatus(vaccine.expiry_date);
                          return (
                            <div 
                              key={vaccine.vaccine_id}
                              className="flex items-center justify-between p-4 bg-[#F5F5F7] rounded-2xl group"
                            >
                              <div className="flex items-center gap-4">
                                <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${
                                  status.color === 'red' ? 'bg-red-100' :
                                  status.color === 'amber' ? 'bg-amber-100' : 'bg-emerald-100'
                                }`}>
                                  <Syringe size={20} className={
                                    status.color === 'red' ? 'text-red-600' :
                                    status.color === 'amber' ? 'text-amber-600' : 'text-emerald-600'
                                  } />
                                </div>
                                <div>
                                  <p className="font-semibold text-[#1D1D1F]">{vaccine.vaccine_name}</p>
                                  <p className="text-sm text-[#86868B]">
                                    {vaccine.date_administered && new Date(vaccine.date_administered).toLocaleDateString('it-IT')}
                                    {vaccine.vet_name && ` • ${vaccine.vet_name}`}
                                  </p>
                                </div>
                              </div>
                              <div className="flex items-center gap-3">
                                <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                                  status.color === 'red' ? 'bg-red-100 text-red-700' :
                                  status.color === 'amber' ? 'bg-amber-100 text-amber-700' : 'bg-emerald-100 text-emerald-700'
                                }`}>
                                  {status.label}
                                </span>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  onClick={() => handleDeleteVaccine(vaccine.vaccine_id)}
                                  className="opacity-0 group-hover:opacity-100 h-8 w-8 text-red-500 hover:bg-red-50"
                                >
                                  <Trash2 size={16} />
                                </Button>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </TabsContent>

                  {/* Documents Tab */}
                  <TabsContent value="documents" className="mt-6">
                    <div className="flex justify-between items-center mb-4">
                      <h4 className="font-semibold text-[#1D1D1F]">Documenti</h4>
                      <Button 
                        onClick={() => setShowDocUploader(true)}
                        className="bg-[#FF6B4A] hover:bg-[#E05A3A] rounded-xl"
                        size="sm"
                      >
                        <Upload size={16} className="mr-1" />
                        Carica
                      </Button>
                    </div>
                    
                    {documents.length === 0 ? (
                      <div className="text-center py-8 bg-[#F5F5F7] rounded-2xl">
                        <FileText size={40} className="mx-auto mb-3 text-[#D1D1D6]" />
                        <p className="text-[#86868B]">Nessun documento caricato</p>
                        <Button 
                          onClick={() => setShowDocUploader(true)}
                          variant="outline"
                          className="mt-3 rounded-xl"
                        >
                          Carica primo documento
                        </Button>
                      </div>
                    ) : (
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        {documents.map((doc) => (
                          <div 
                            key={doc.document_id}
                            className="flex items-center justify-between p-4 bg-[#F5F5F7] rounded-2xl group"
                          >
                            <div className="flex items-center gap-3">
                              <div className="w-10 h-10 rounded-xl bg-blue-100 flex items-center justify-center">
                                <FileText size={20} className="text-blue-600" />
                              </div>
                              <div>
                                <p className="font-semibold text-[#1D1D1F] truncate max-w-[150px]">{doc.title || doc.file_name}</p>
                                <p className="text-xs text-[#86868B]">
                                  {doc.created_at && new Date(doc.created_at).toLocaleDateString('it-IT')}
                                </p>
                              </div>
                            </div>
                            <div className="flex gap-1">
                              {doc.file_url && (
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  onClick={() => window.open(doc.file_url.startsWith('/') ? `${API.replace('/api', '')}${doc.file_url}` : doc.file_url, '_blank')}
                                  className="h-8 w-8 text-[#86868B] hover:text-[#1D1D1F]"
                                >
                                  <Eye size={16} />
                                </Button>
                              )}
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => handleDeleteDocument(doc.document_id)}
                                className="h-8 w-8 text-red-500 hover:bg-red-50 opacity-0 group-hover:opacity-100"
                              >
                                <Trash2 size={16} />
                              </Button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </TabsContent>
                </Tabs>
              </Card>

              {/* Notes */}
              {selectedPet.notes && (
                <Card>
                  <h3 className="text-lg font-semibold text-[#1D1D1F] dark:text-white mb-3">
                    Note
                  </h3>
                  <p className="text-[#6E6E73] dark:text-gray-400">{selectedPet.notes}</p>
                </Card>
              )}
            </div>
          )}
        </div>
      )}

      {/* Add Pet Dialog */}
      <Dialog open={showAddPet} onOpenChange={setShowAddPet}>
        <DialogContent className="max-w-md rounded-3xl">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold">Aggiungi Animale</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleAddPet} className="space-y-4">
            <div>
              <Label>Nome *</Label>
              <Input
                required
                value={newPet.name}
                onChange={(e) => setNewPet({ ...newPet, name: e.target.value })}
                placeholder="Es. Fido"
                className="rounded-xl"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Specie *</Label>
                <Select value={newPet.species} onValueChange={(v) => setNewPet({ ...newPet, species: v })}>
                  <SelectTrigger className="rounded-xl">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="dog">Cane</SelectItem>
                    <SelectItem value="cat">Gatto</SelectItem>
                    <SelectItem value="bird">Uccello</SelectItem>
                    <SelectItem value="rabbit">Coniglio</SelectItem>
                    <SelectItem value="other">Altro</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Razza</Label>
                <Input
                  value={newPet.breed}
                  onChange={(e) => setNewPet({ ...newPet, breed: e.target.value })}
                  placeholder="Es. Labrador"
                  className="rounded-xl"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Data di nascita</Label>
                <Input
                  type="date"
                  value={newPet.birth_date}
                  onChange={(e) => setNewPet({ ...newPet, birth_date: e.target.value })}
                  className="rounded-xl"
                />
              </div>
              <div>
                <Label>Peso (kg)</Label>
                <Input
                  type="number"
                  step="0.1"
                  value={newPet.weight}
                  onChange={(e) => setNewPet({ ...newPet, weight: e.target.value })}
                  placeholder="Es. 25.5"
                  className="rounded-xl"
                />
              </div>
            </div>
            <div>
              <Label>Microchip</Label>
              <Input
                value={newPet.microchip}
                onChange={(e) => setNewPet({ ...newPet, microchip: e.target.value })}
                placeholder="Numero microchip"
                className="rounded-xl"
              />
            </div>
            <div>
              <Label>Colore</Label>
              <Input
                value={newPet.color}
                onChange={(e) => setNewPet({ ...newPet, color: e.target.value })}
                placeholder="Es. Marrone"
                className="rounded-xl"
              />
            </div>
            <Button
              type="submit"
              disabled={isSubmitting || !newPet.name}
              className="w-full bg-[#FF6B4A] hover:bg-[#E05A3A] rounded-xl"
            >
              {isSubmitting ? "Aggiunta in corso..." : "Aggiungi Animale"}
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit Pet Dialog */}
      <Dialog open={showEditPet} onOpenChange={setShowEditPet}>
        <DialogContent className="max-w-md rounded-3xl">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold">Modifica {selectedPet?.name}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleUpdatePet} className="space-y-4">
            <div>
              <Label>Nome *</Label>
              <Input
                required
                value={newPet.name}
                onChange={(e) => setNewPet({ ...newPet, name: e.target.value })}
                className="rounded-xl"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Specie</Label>
                <Select value={newPet.species} onValueChange={(v) => setNewPet({ ...newPet, species: v })}>
                  <SelectTrigger className="rounded-xl">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="dog">Cane</SelectItem>
                    <SelectItem value="cat">Gatto</SelectItem>
                    <SelectItem value="bird">Uccello</SelectItem>
                    <SelectItem value="rabbit">Coniglio</SelectItem>
                    <SelectItem value="other">Altro</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Razza</Label>
                <Input
                  value={newPet.breed || ''}
                  onChange={(e) => setNewPet({ ...newPet, breed: e.target.value })}
                  className="rounded-xl"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Data di nascita</Label>
                <Input
                  type="date"
                  value={newPet.birth_date || ''}
                  onChange={(e) => setNewPet({ ...newPet, birth_date: e.target.value })}
                  className="rounded-xl"
                />
              </div>
              <div>
                <Label>Peso (kg)</Label>
                <Input
                  type="number"
                  step="0.1"
                  value={newPet.weight || ''}
                  onChange={(e) => setNewPet({ ...newPet, weight: e.target.value })}
                  className="rounded-xl"
                />
              </div>
            </div>
            <div>
              <Label>Microchip</Label>
              <Input
                value={newPet.microchip || ''}
                onChange={(e) => setNewPet({ ...newPet, microchip: e.target.value })}
                className="rounded-xl"
              />
            </div>
            <div>
              <Label>Colore</Label>
              <Input
                value={newPet.color || ''}
                onChange={(e) => setNewPet({ ...newPet, color: e.target.value })}
                className="rounded-xl"
              />
            </div>
            <Button
              type="submit"
              disabled={isSubmitting}
              className="w-full bg-[#FF6B4A] hover:bg-[#E05A3A] rounded-xl"
            >
              {isSubmitting ? "Salvataggio..." : "Salva Modifiche"}
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* QR Code Full Dialog */}
      <Dialog open={showQRCode} onOpenChange={setShowQRCode}>
        <DialogContent className="max-w-sm rounded-3xl">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold text-center">QR Code di {selectedPet?.name}</DialogTitle>
          </DialogHeader>
          <div className="flex flex-col items-center py-6">
            <div className="p-6 bg-white rounded-2xl shadow-sm border">
              <QRCodeSVG value={shareUrl} size={200} level="H" />
            </div>
            <p className="text-sm text-[#86868B] mt-4 text-center">
              Scansiona questo codice per visualizzare<br />il profilo pubblico di {selectedPet?.name}
            </p>
            <div className="flex gap-3 mt-6">
              <Button
                variant="outline"
                onClick={() => {
                  navigator.clipboard.writeText(shareUrl);
                  toast.success("Link copiato!");
                }}
                className="rounded-xl"
              >
                <Share2 size={16} className="mr-2" />
                Copia Link
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <Dialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
        <DialogContent className="max-w-sm rounded-3xl">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold">Conferma eliminazione</DialogTitle>
          </DialogHeader>
          <p className="text-[#6E6E73] py-4">
            Sei sicuro di voler eliminare <strong>{selectedPet?.name}</strong>? Questa azione non può essere annullata.
          </p>
          <div className="flex gap-3">
            <Button
              variant="outline"
              onClick={() => setShowDeleteConfirm(false)}
              className="flex-1 rounded-xl"
            >
              Annulla
            </Button>
            <Button
              onClick={handleDeletePet}
              variant="destructive"
              className="flex-1 rounded-xl"
            >
              Elimina
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Image Cropper */}
      <ImageCropper
        open={showCropper}
        onClose={() => setShowCropper(false)}
        imageSrc={cropImage}
        onCropComplete={(blob) => {
          handlePhotoUpload(blob);
          setShowCropper(false);
        }}
      />

      {/* Add Vaccine Dialog */}
      <Dialog open={showAddVaccine} onOpenChange={setShowAddVaccine}>
        <DialogContent className="max-w-md rounded-3xl">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold flex items-center gap-2">
              <Syringe size={20} className="text-emerald-500" />
              Aggiungi Vaccino
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleAddVaccine} className="space-y-4">
            <div>
              <Label>Nome Vaccino *</Label>
              <Input
                required
                value={newVaccine.vaccine_name}
                onChange={(e) => setNewVaccine({ ...newVaccine, vaccine_name: e.target.value })}
                placeholder="Es. Antirabbica"
                className="rounded-xl"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Data somministrazione *</Label>
                <Input
                  type="date"
                  required
                  value={newVaccine.date_administered}
                  onChange={(e) => setNewVaccine({ ...newVaccine, date_administered: e.target.value })}
                  className="rounded-xl"
                />
              </div>
              <div>
                <Label>Data scadenza</Label>
                <Input
                  type="date"
                  value={newVaccine.expiry_date}
                  onChange={(e) => setNewVaccine({ ...newVaccine, expiry_date: e.target.value })}
                  className="rounded-xl"
                />
              </div>
            </div>
            <div>
              <Label>Veterinario</Label>
              <Input
                value={newVaccine.vet_name}
                onChange={(e) => setNewVaccine({ ...newVaccine, vet_name: e.target.value })}
                placeholder="Nome veterinario"
                className="rounded-xl"
              />
            </div>
            <div>
              <Label>Numero lotto</Label>
              <Input
                value={newVaccine.batch_number}
                onChange={(e) => setNewVaccine({ ...newVaccine, batch_number: e.target.value })}
                placeholder="Es. ABC123"
                className="rounded-xl"
              />
            </div>
            <Button
              type="submit"
              disabled={isSubmitting || !newVaccine.vaccine_name || !newVaccine.date_administered}
              className="w-full bg-emerald-500 hover:bg-emerald-600 rounded-xl"
            >
              {isSubmitting ? "Aggiunta in corso..." : "Aggiungi Vaccino"}
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Document Uploader Dialog */}
      <Dialog open={showDocUploader} onOpenChange={setShowDocUploader}>
        <DialogContent className="sm:max-w-lg rounded-3xl">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold flex items-center gap-2">
              <Upload size={20} className="text-[#FF6B4A]" />
              Carica documento per {selectedPet?.name}
            </DialogTitle>
          </DialogHeader>
          
          {selectedPet && (
            <DocumentUploader
              petId={selectedPet.pet_id}
              petName={selectedPet.name}
              onUploadComplete={() => {
                setShowDocUploader(false);
                fetchPetDetails(selectedPet.pet_id);
                toast.success("Documento caricato!");
              }}
            />
          )}
        </DialogContent>
      </Dialog>
    </PageLayout>
  );
}
